import { useState } from 'react';

import { CustomFieldValueData } from 'src/features/crm/types/type';
import { CustomFieldSelectPopover } from 'src/features/custom-fields/CustomFieldSelectPopover';
import { CustomFieldSelectValue } from 'src/features/custom-fields/CustomFieldSelectValue';
import {
  createSelectOption,
  removeSelectOption,
  renameSelectOption,
  SELECT_PICKER_COPY,
} from 'src/features/custom-fields/selectOptions';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
import {
  CustomFieldOperationType,
  MultiSelectCustomField,
} from 'src/store/clients/types';

interface Props {
  field: MultiSelectCustomField;
  value: unknown;
  placeholder: string;
  onSaveProperty: (
    property: MultiSelectCustomField,
    deletedOptionKey?: string,
    operationType?: CustomFieldOperationType,
  ) => Promise<void>;
  onSaveValue: (data: CustomFieldValueData) => void;
}

export const SelectInput = ({
  field,
  value,
  placeholder,
  onSaveProperty,
  onSaveValue,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const options = field.options ?? [];
  // A value whose option was removed shows as empty.
  const selected = options.find((option) => option.id === value);

  const saveOptions = (
    next: MultiSelectOption[],
    deletedOptionKey?: string,
    operationType?: CustomFieldOperationType,
  ) =>
    onSaveProperty(
      { ...field, options: next },
      deletedOptionKey,
      operationType,
    );
  const saveValue = (optionId: string) =>
    onSaveValue({ fieldId: field.id, value: optionId });

  return (
    <div className="flex min-h-8 w-full min-w-0 items-center">
      <CustomFieldSelectValue
        label={selected?.label}
        placeholder={placeholder}
        onOpen={setAnchorEl}
      />
      <CustomFieldSelectPopover
        anchorEl={anchorEl}
        fieldName={field.name}
        options={options}
        selectedId={selected?.id}
        onSelect={saveValue}
        onClear={() => saveValue('')}
        onAddOption={async (label) => {
          const newOption = createSelectOption(label, options.length);
          await saveOptions([...options, newOption], undefined, 'createOption');
          saveValue(newOption.id);
        }}
        onRenameOption={(optionId, label) =>
          void saveOptions(
            renameSelectOption(options, optionId, label),
            undefined,
            'updateOption',
          )
        }
        onRemoveOption={(optionId) =>
          void saveOptions(
            removeSelectOption(options, optionId),
            optionId,
            'deleteOption',
          )
        }
        onClose={() => setAnchorEl(null)}
        copy={SELECT_PICKER_COPY}
      />
    </div>
  );
};
